import { Component, ElementRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { HrService } from 'src/app/hr/hr.service';
import { CommonService } from 'src/app/shared/service/common.service';
import * as d3 from 'd3';
import { HttpStatusCode } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { savePostLoginRedirect } from 'src/app/shared/utils/post-login-redirect';

type SrReportSection = 'finalSummary' | 'resumeMatching' | 'assessment' | 'interviewBot';

interface SrCandidate {
  candidateId?: string;
  email?: string;
  phone?: string;
  jobApplicationId?: string;
}

@Component({
  selector: 'app-global-assessment',
  templateUrl: './global-assessment.component.html',
  styleUrls: ['./global-assessment.component.scss'],
  standalone: false
})
export class GlobalAssessmentComponent {
  userAssessmentDriveId: any;
  driveAssessmentData: any;
  driveReportResponseList: any;
  loading: boolean = true;
  loadFailed: boolean = false;
  /** 400/404 from the report API: bad, tampered or orphaned token. */
  reportNotFound: boolean = false;
  candidateAudioData: any;
  softSkillData: any;
  showError: boolean;
  private hostElement: HTMLElement;

  constructor(
    public _commonServices: CommonService,
    private _title: Title,
    private hrService: HrService,
    private elementRef: ElementRef,
    private route: ActivatedRoute
  ) { this._title.setTitle(`${_commonServices.Enums.BRAND_NAME}  || View-Details`);
    this.hostElement = this.elementRef.nativeElement;}

  ngOnInit(): void {
    // Read through the router: after the HR signs in we arrive here by in-app
    // navigation, and window.location may not have caught up yet.
    const token = (
      this.route.snapshot.queryParamMap.get('token') ??
      new URLSearchParams(window.location.search).get('token')
    )?.replace(/ /g, '+');

    if (token) {
      sessionStorage.setItem('globalToken', token);
    }

    this.getGlobalReportData();
  }

  // ── Which report this is ───────────────────────────────────────────────
  // One API, two contracts. SR links (Workday resultUrl) send data.reportType
  // with one section per stage. Older drive links send the DriveUserResponseDto
  // with no reportType: the plain assessment report, or the interview-bot
  // stage when isInterviewBotDrive is set.
  get reportKind(): 'sr' | 'bot' | 'assessment' {
    const data = this.driveAssessmentData;
    if (data?.reportType) {
      return 'sr';
    }
    if (data?.isInterviewBotDrive) {
      return 'bot';
    }
    return 'assessment';
  }

  // Sections each SR report type can carry. A listed section may still be
  // null when its stage has no data yet (e.g. interviewBot before the call is
  // evaluated); it then shows "Not available". Unlisted sections are always null.
  private readonly SR_REPORT_SECTIONS: Record<string, SrReportSection[]> = {
    RESUME_REPORT: ['resumeMatching'],
    ASSESSMENT: ['resumeMatching', 'assessment'],
    INTERVIEW: ['resumeMatching', 'assessment', 'interviewBot'],
    FINAL_REPORT: ['finalSummary', 'resumeMatching', 'assessment', 'interviewBot'],
  };

  private readonly SR_REPORT_LABELS: Record<string, string> = {
    RESUME_REPORT: 'Resume report',
    ASSESSMENT: 'Assessment report',
    INTERVIEW: 'Interview report',
    FINAL_REPORT: 'Final summary',
  };

  /** Badge next to the candidate name; empty for the plain assessment report. */
  get reportLabel(): string {
    if (this.reportKind === 'sr') {
      return this.SR_REPORT_LABELS[this.driveAssessmentData?.reportType] ?? 'Candidate report';
    }
    return this.reportKind === 'bot' ? 'Interview bot' : '';
  }

  /** The report type covers this section, but the backend has no data for it yet. */
  isSectionMissing(section: SrReportSection): boolean {
    const data = this.driveAssessmentData;
    const expected = this.SR_REPORT_SECTIONS[data?.reportType] ?? [];
    return expected.includes(section) && data?.[section] == null;
  }

  /**
   * Identity rows for an SR report. The final summary carries the fullest
   * record; earlier report types only have resumeMatching. Set on load.
   */
  srCandidate: SrCandidate | null = null;

  private toSrCandidate(data: any): SrCandidate {
    const summary = data?.finalSummary;
    const resume = data?.resumeMatching;
    return {
      candidateId:
        summary?.candidateId ??
        resume?.candidateId ??
        data?.assessment?.candidateId ??
        data?.interviewBot?.candidateId,
      email: summary?.email ?? resume?.email,
      phone: summary?.phone ?? resume?.phone,
      jobApplicationId: summary?.jobApplicationId ?? resume?.jobApplicationId,
    };
  }

  /** Name for the header and avatar, wherever this payload keeps it. */
  get candidateName(): string {
    const data = this.driveAssessmentData;
    return (
      data?.finalSummary?.name ??
      data?.resumeMatching?.name ??
      data?.userDto?.name ??
      data?.interviewBot?.candidateName ??
      data?.assessment?.candidateName ??
      ''
    );
  }

  /** Second breadcrumb item: the drive, or the SR for an SR report. */
  get reportContext(): string {
    const data = this.driveAssessmentData;
    if (this.reportKind === 'sr') {
      return data?.resumeMatching?.srNumber || data?.interviewBot?.srNumber || '';
    }
    return data?.driveName ?? '';
  }

  /** API sometimes doubles the dial code ("+91-+9190..."); keep one leading +. */
  cleanPhone(value: string): string {
    const phone = String(value ?? '').trim();
    if (!phone) {
      return '';
    }
    const plusAt = phone.lastIndexOf('+');
    return plusAt > 0 ? phone.slice(plusAt) : phone;
  }

  // ── Interview-bot stage ────────────────────────────────────────────────
  // Lateral-bot topics are scored 0-10 each; the overall is out of
  // topics x 10, same as the HR view-candidate page.
  readonly BOT_TOPIC_MAX = 10;

  /** Soft-skill scores, one entry per scored video answer. */
  audioScores: any[] = [];

  // Bot-stage transcript: the report payload only carries the callId, so the
  // conversation is fetched separately, same endpoint as view-candidate.
  botConversation: { role?: string; message?: string }[] = [];
  botTranscriptState: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';

  /** Tab / window switches above this flag the check, as on view-candidate. */
  readonly TAB_SWITCH_LIMIT = 3;

  get botTopics(): { topicName: string; score: number }[] {
    return this.driveAssessmentData?.interviewBotTopicScores ?? [];
  }

  get botTotal(): { score: number; max: number } | null {
    const topics = this.botTopics;
    if (!topics.length) {
      return null;
    }
    const score = topics.reduce((sum, topic) => sum + (Number(topic?.score) || 0), 0);
    return { score, max: topics.length * this.BOT_TOPIC_MAX };
  }

  /** Bar colour for a 0-10 topic score. */
  topicTone(score: number): string {
    const value = Number(score) || 0;
    if (value >= 7) {
      return 'bg-green_600';
    }
    if (value >= 4) {
      return 'bg-status_amber';
    }
    return 'bg-red_500';
  }

  /** Accepts the list the API sends (and a lone object, defensively). */
  private toAudioScoreList(value: any): any[] {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }
    return value ? [value] : [];
  }

  /**
   * Radar axes = the soft skills the API actually scored (0-100). The older
   * prepareSpiderChartData() looks up skill and section names this payload
   * never sends, so every axis came out as 0.
   */
  private toRadarData(entry: any): { skillName: string; totalScore: number }[] {
    return (entry?.skills ?? [])
      .filter((skill) => skill?.skillName)
      .map((skill) => ({
        skillName: skill.skillName,
        totalScore: Number.isFinite(Number(skill.totalScore)) ? Number(skill.totalScore) : 0,
      }));
  }

  /** Culture-fit verdict from the first scored answer that has one. */
  get cultureFit(): any {
    return this.audioScores.find((entry) => entry?.fffScore)?.fffScore ?? null;
  }

  // ── Feeds ──────────────────────────────────────────────────────────────
  // Until a feed is merged the API puts "Video merging in progress" where the
  // URL goes, so only a real URL is handed to the video element.
  isFeedReady(key: string): boolean {
    const url = this.driveAssessmentData?.urlsForResponse?.[key];
    return typeof url === 'string' && /^https?:\/\//i.test(url);
  }

  // ── Question answers (reportResponseList) ─────────────────────────────
  /** What the candidate answered: picked option, or typed/FIB answer. */
  answerGiven(question: any): string | null {
    return question?.optionSelected ?? question?.answerByUser ?? null;
  }

  /** Expected answer: correct option (MCQ), FIB answer, or boolean answer. */
  expectedAnswer(question: any): string | null {
    if (question?.correctOption != null) {
      return question.correctOption;
    }
    if (question?.fibAnswer != null) {
      return question.fibAnswer;
    }
    return question?.booleanTypeResponse != null ? String(question.booleanTypeResponse) : null;
  }

  /** Video / audio answers are scored, not matched against an expected answer. */
  isMediaQuestion(question: any): boolean {
    return ['VIDEO', 'AUDIO', 'TECHNICAL_QUESTION'].includes(question?.questionType);
  }

  // ── Bot proctoring lists ──────────────────────────────────────────────
  detectionLabel(detected: boolean, names: string[] | null, clearLabel: string): string {
    if (!detected) {
      return clearLabel;
    }
    return names?.length ? `Detected (${names.join(', ')})` : 'Detected';
  }

  // ── Resume matching (final report) ────────────────────────────────────
  resumeStatusTone(status: string): string {
    switch (String(status ?? '').toUpperCase()) {
      case 'SHORTLISTED':
        return 'status-pill--done';
      case 'NOT_SHORTLISTED':
        return 'status-pill--failed';
      case 'PENDING':
        return 'status-pill--ongoing';
      default:
        return 'status-pill--idle';
    }
  }

  /** Must-have / preferred / nice-to-have rows for the skills table. */
  get resumeSkillGroups(): { label: string; score: number; matched: string[]; missing: string[] }[] {
    const match = this.driveAssessmentData?.resumeMatching;
    if (!match) {
      return [];
    }
    return [
      { label: 'Must-have', score: match.mustHaveScore, matched: match.mustHaveMatched ?? [], missing: match.mustHaveMissing ?? [] },
      { label: 'Preferred', score: match.preferredScore, matched: match.preferredMatched ?? [], missing: match.preferredMissing ?? [] },
      { label: 'Nice-to-have', score: match.niceToHaveScore, matched: match.niceToHaveMatched ?? [], missing: match.niceToHaveMissing ?? [] },
    ];
  }

  /** Bot vs candidate side of a chat bubble; system/tool entries are not shown. */
  isBotMessage(message: { role?: string }): boolean {
    return message?.role === 'bot' || message?.role === 'assistant';
  }

  isCandidateMessage(message: { role?: string }): boolean {
    return message?.role === 'user';
  }

  private loadBotTranscript(callId: string): void {
    if (!callId) {
      return;
    }
    this.botTranscriptState = 'loading';
    this.hrService.getChatBetweenBotAndCandidate(callId).subscribe({
      next: (response: any) => {
        if (response?.status !== 'OK') {
          this.botTranscriptState = 'failed';
          return;
        }
        const messages = response?.data?.artifact?.messages;
        this.botConversation = (Array.isArray(messages) ? messages : []).filter(
          (m) =>
            (this.isBotMessage(m) || this.isCandidateMessage(m)) &&
            String(m?.message ?? '').trim() !== ''
        );
        this.botTranscriptState = 'ready';
      },
      error: () => {
        this.botTranscriptState = 'failed';
      },
    });
  }

  topicBarWidth(score: number): number {
    const value = Number(score) || 0;
    return Math.max(0, Math.min(100, (value / this.BOT_TOPIC_MAX) * 100));
  }

  // ── Final summary ──────────────────────────────────────────────────────
  get finalSummary(): any {
    return this.driveAssessmentData?.finalSummary;
  }

  get finalAssessment(): any {
    return this.driveAssessmentData?.assessment;
  }

  get finalBot(): any {
    return this.driveAssessmentData?.interviewBot;
  }

  // Proctoring verdicts arrive as free-form enum strings; labels and tones
  // match the SR candidate scorecard and view-candidate.
  private readonly PROCTORING_LABELS: Record<string, string> = {
    clear: 'Clear',
    passed: 'Clear',
    'proctoring passed': 'Clear',
    suspicious: 'Suspicious',
    failed: 'Suspicious',
    'proctoring failed': 'Suspicious',
    'highly suspicious': 'Highly Suspicious',
    cheating: 'Cheating',
  };

  proctoringLabel(verdict: any): string {
    const key = String(verdict ?? '').trim().toLowerCase().replace(/_/g, ' ');
    if (!key) {
      return 'N/A';
    }
    return (
      this.PROCTORING_LABELS[key] ?? key.replace(/\b\w/g, (c) => c.toUpperCase())
    );
  }

  /** Dot / text colour for a proctoring verdict. */
  proctoringTone(verdict: any): 'green' | 'amber' | 'red' | 'grey' {
    switch (this.proctoringLabel(verdict)) {
      case 'Clear':
        return 'green';
      case 'Suspicious':
        return 'amber';
      case 'Highly Suspicious':
      case 'Cheating':
        return 'red';
      default:
        return 'grey';
    }
  }

  /** Number with at most two decimals, or "-" when missing. */
  fmt(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    const num = Number(value);
    return Number.isFinite(num) ? String(Math.round(num * 100) / 100) : String(value);
  }

  /** The candidate picked the expected answer. */
  isCorrectAnswer(question: any): boolean {
    return (
      !!question?.selectedAnswer &&
      question?.selectedAnswer === question?.correctAnswer
    );
  }

  /** Two-letter avatar text, e.g. "First Last" -> "FL". */
  getInitials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '-';
    }
    const first = parts[0][0];
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  /** Minutes between start and end, or null when either time is missing. */
  get timeTakenMinutes(): number | null {
    const start = this.driveAssessmentData?.userDto?.startTime;
    const end = this.driveAssessmentData?.userDto?.endTime;
    if (!this.isValidDate(start) || !this.isValidDate(end)) {
      return null;
    }
    const minutes = Math.round(
      (new Date(end).getTime() - new Date(start).getTime()) / 60000
    );
    return minutes >= 0 ? minutes : null;
  }

  /** How many feed tiles the Feeds card will actually render. */
  get visibleFeedCount(): number {
    const urls = this.driveAssessmentData?.urlsForResponse ?? {};
    const status = this.mergeVideoStatus ?? {};
    const merged = ['WEB_FEED', 'MOBILE_FEED', 'SCREEN_SHARE', 'SURROUNDING_FEED'].filter(
      (key) => urls[key] && status[key] !== 'NoChunks'
    ).length;
    return merged + (urls['AUDIO_FEED'] ? 1 : 0);
  }

  /** Sections that carry a recorded popup-question answer. */
  get popupQuestions(): any[] {
    return (this.driveAssessmentData?.userDto?.sectionScoreDtoList ?? []).filter(
      (section) => section?.selectedRandomQuestionExplainedUrl
    );
  }

  /**
   * Picks one of the theme's existing badge tones for a section status.
   * Matched on substrings because the backend sends several spellings
   * (COMPLETED, PARTIALLY_EVALUATED, AWAITING_REVIEW, ...).
   */
  sectionStatusTone(status: string): string {
    const value = (status || '').toUpperCase();
    if (value === 'STARTED') {
      return 'status-pill--ongoing';
    }
    if (
      value.includes('COMPLETE') ||
      value.includes('EVALUATED') ||
      value.includes('SUBMIT') ||
      value.includes('PASS')
    ) {
      return 'status-pill--done';
    }
    if (
      value.includes('PROGRESS') ||
      value.includes('PARTIAL') ||
      value.includes('AWAIT') ||
      value.includes('PENDING') ||
      value.includes('REVIEW')
    ) {
      return 'status-pill--ongoing';
    }
    return 'status-pill--idle';
  }

  getGlobalReportData() {
    const token = sessionStorage.getItem('globalToken');
    this._commonServices
      .getPublicAssessmentDetails(token)
      .subscribe((response: any) => {
        if (response && response.statusCode === 200) {
          this.driveAssessmentData = response.data;
          if (this.reportKind === 'sr') {
            // SR contract: sections only, none of the drive-report keys below.
            this.srCandidate = this.toSrCandidate(response.data);
            this.loading = false;
            return;
          }
          this.driveReportResponseList = response.data?.reportResponseList;
          this.mergeVideoStatus = response.data?.mergedVideoStatus;
          // The bot-stage and final-report payloads omit some of these keys;
          // Object.keys(undefined) used to throw here and leave the page on
          // "Loading report..." forever.
          this.urlsResponseLength = Object.keys(
            this.driveAssessmentData?.urlsForResponse ?? {}
          ).length;
          this.isQuestionExplanationResponse = (
            this.driveAssessmentData?.userDto?.sectionScoreDtoList ?? []
          ).every((video) => video?.selectedRandomQuestionExplainedUrl == null);
          // candidateAudioScoreDto is a list: one entry per scored video answer.
          this.audioScores = this.toAudioScoreList(response.data?.candidateAudioScoreDto);
          this.softSkillData =
            this.audioScores.find((entry) => entry?.skills?.length) ?? this.audioScores[0];
          this.candidateAudioData = this.softSkillData?.skills;
          if (this.candidateAudioData) {
            // The radar is an extra: if its data is malformed, report it and
            // still show the rest of the report instead of an endless spinner.
            try {
              const spiderData = this.toRadarData(this.softSkillData);
              setTimeout(() => {
                try {
                  this.createRadarChart(spiderData);
                } catch (chartError) {
                  Sentry.captureException(chartError, { tags: { stage: 'public-report-radar' } });
                }
              });
            } catch (chartError) {
              Sentry.captureException(chartError, { tags: { stage: 'public-report-radar' } });
            }
          }
          if (this.softSkillData?.error) {
            this.showError = true;
          } else {
            this.showError = false;
          }

          if (this.reportKind === 'bot') {
            this.loadBotTranscript(this.driveAssessmentData?.callId);
          }

          this.loading = false;
        } else {
          // Nothing to render, but the spinner must not outlive the request.
          this.loading = false;
          this.loadFailed = true;
          this.reportNotFound =
            response?.statusCode === HttpStatusCode.NotFound ||
            response?.statusCode === HttpStatusCode.BadRequest;
        }
      },
      (error) => {
        this.loading = false;
        this.loadFailed = true;
        // Both carry "Report not found": 404 for a bad or foreign token, 400
        // when the token is fine but its attempt/candidate/resume is gone.
        const status = error?.status ?? error?.error?.statusCode;
        this.reportNotFound =
          status === HttpStatusCode.NotFound || status === HttpStatusCode.BadRequest;
        // An expired HR session comes back as 401; the error interceptor clears
        // storage and sends them to sign-in. Keep this link so they return here.
        const unauthorized =
          error?.status === HttpStatusCode.Unauthorized ||
          error?.error?.statusCode === HttpStatusCode.Unauthorized;
        if (unauthorized && token) {
          savePostLoginRedirect(
            '/assessment/public?token=' + encodeURIComponent(token)
          );
        }
      });
  }

  private createRadarChart(chartData): void {
    const margin = { top: 70, right: 90, bottom: 40, left: 90 };
    const width = 900 - margin.left - margin.right;
    const height = 600 - margin.top - margin.bottom;
    const radius = Math.min(width / 2, height / 2);
    const total = chartData?.length;
    const angleSlice = (Math.PI * 2) / total;

    const rScale = d3.scaleLinear().range([0, radius]).domain([0, 100]);

    d3.select(this.hostElement).select('svg').remove();

    const svg = d3
      .select(this.hostElement.querySelector('.radar-chart-container'))
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr(
        'transform',
        `translate(${width / 2 + margin.left}, ${height / 2 + margin.top})`
      );

      

    // Background circles
    const axisGrid = svg.append('g').attr('class', 'axis-grid');
    const gridLevels = 5;
    axisGrid
      .selectAll('.radar-chart-circle')
      .data(d3.range(1, gridLevels + 1).reverse())
      .enter()
      .append('circle')
      .attr('class', 'radar-chart-circle')
      .attr('r', (d) => (radius * d) / gridLevels)
      .style('fill', '#FCD201')
      .style('fill-opacity', 0.2);

    // Circle value labels
    axisGrid
      .selectAll('.radar-chart-value')
      .data(d3.range(1, gridLevels + 1).reverse())
      .enter()
      .append('text')
      .attr('class', 'radar-value-label')
      .attr('x', 4)
      .attr('y', (d) => (-d * radius) / gridLevels)
      .attr('dy', '0.4em')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text((d) => Math.round((d * 100) / gridLevels));

    // Draw axes
    const axes = svg.append('g').attr('class', 'axes');
    axes
      .selectAll('.radar-chart-axis')
      .data(chartData)
      .enter()
      .append('line')
      .attr('class', 'radar-chart-axis')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr(
        'x2',
        (d, i) => rScale(100) * Math.cos(angleSlice * i - Math.PI / 2)
      )
      .attr(
        'y2',
        (d, i) => rScale(100) * Math.sin(angleSlice * i - Math.PI / 2)
      )
      .style('stroke', '#999')
      .style('stroke-width', 1);

    // Function to calculate label position with specific adjustments for targeted labels
    const calculateLabelPosition = (i, skillName) => {
      const angle = angleSlice * i - Math.PI / 2;
      let offsetMultiplier;

      // Specific adjustments for English Grammar and Engagement and Persuasiveness
      if (skillName === 'English Grammar') {
        offsetMultiplier = 1.03; // Move English Grammar closer to circumference
      } else if (skillName === 'Engagement and Persuasiveness') {
        offsetMultiplier = 1.03; // Move Engagement and Persuasiveness closer to circumference
      } else if (Math.abs(angle) < 0.3 || Math.abs(angle - Math.PI) < 0.3) {
        offsetMultiplier = 1.2; // Keep other middle labels further out
      } else {
        offsetMultiplier = 1.08; // Standard position for other labels
      }

      const labelRadius = radius * offsetMultiplier;

      return {
        x: labelRadius * Math.cos(angle),
        y: labelRadius * Math.sin(angle),
        angle: angle,
      };
    };

    // Add axis labels with improved positioning
    axes
      .selectAll('.radar-chart-label')
      .data(chartData)
      .enter()
      .append('text')
      .attr('class', 'radar-chart-label')
      .each(function (d: any, i) {
        const pos = calculateLabelPosition(i, d.skillName);
        const angle = pos.angle;

        // Determine text anchor based on position
        let textAnchor;
        if (Math.abs(angle) < 0.1 || Math.abs(angle - Math.PI) < 0.1) {
          textAnchor = 'middle';
        } else if (angle > Math.PI / 2 && angle < (3 * Math.PI) / 2) {
          textAnchor = 'end';
        } else {
          textAnchor = 'start';
        }

        d3.select(this)
          .attr('x', pos.x)
          .attr('y', pos.y)
          .attr('text-anchor', textAnchor)
          .attr('dy', () => {
            // Special handling for top and bottom labels
            if (d.skillName === 'English Grammar') return '-0.5em';
            if (d.skillName === 'Engagement and Persuasiveness') return '1em';
            if (Math.abs(angle) < 0.1) return '-1em';
            if (Math.abs(angle - Math.PI) < 0.1) return '1.5em';
            return '0.35em';
          });
      })
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text((d: any) => d.skillName);

    // For long axis labels, implement text wrapping
    axes.selectAll('.radar-chart-label').each(function () {
      const text = d3.select(this);
      const words = text.text().split(/\s+/);

      if (words.length > 2) {
        text.text('');
        const firstLine = words.slice(0, Math.ceil(words.length / 2)).join(' ');
        const secondLine = words.slice(Math.ceil(words.length / 2)).join(' ');

        text
          .append('tspan')
          .attr('x', text.attr('x'))
          .attr('dy', '-0.6em')
          .text(firstLine);

        text
          .append('tspan')
          .attr('x', text.attr('x'))
          .attr('dy', '1.2em')
          .text(secondLine);
      }
    });

    // Special handling for "Engagement and Persuasiveness" label
    axes
      .selectAll('.radar-chart-label')
      .filter((d: any) => d.skillName === 'Engagement and Persuasiveness')
      .each(function () {
        const text = d3.select(this);
        text.text('');

        text
          .append('tspan')
          .attr('x', text.attr('x'))
          .attr('dy', '-0.6em')
          .text('Engagement and');

        text
          .append('tspan')
          .attr('x', text.attr('x'))
          .attr('dy', '1.2em')
          .text('Persuasiveness');
      });

    // Radar area
    const radarLine = d3
      .lineRadial<any>()
      .radius((d) => rScale(d.totalScore))
      .angle((d, i) => i * angleSlice)
      .curve(d3.curveLinearClosed);

    svg
      .append('path')
      .datum(chartData)
      .attr('class', 'radar-chart-area')
      .attr('d', radarLine)
      .style('fill', '#FCD201')
      .style('fill-opacity', 0.7)
      .style('stroke', 'green')
      .style('stroke-width', 2);

    // Data points
    svg
      .selectAll('.radar-chart-dot')
      .data(chartData)
      .enter()
      .append('circle')
      .attr('class', 'radar-chart-dot')
      .attr('r', 6)
      .attr(
        'cx',
        (d: any, i) =>
          rScale(d.totalScore) * Math.cos(angleSlice * i - Math.PI / 2)
      )
      .attr(
        'cy',
        (d: any, i) =>
          rScale(d.totalScore) * Math.sin(angleSlice * i - Math.PI / 2)
      )
      .style('fill', 'green')
      .style('stroke', '#fff')
      .style('stroke-width', 1);

    // Score labels
    svg
      .selectAll('.radar-score-label')
      .data(chartData)
      .enter()
      .append('text')
      .attr('class', 'radar-score-label')
      .attr('x', (d: any, i) => {
        const scorePosition = d.totalScore * 0.85;
        return rScale(scorePosition) * Math.cos(angleSlice * i - Math.PI / 2);
      })
      .attr('y', (d: any, i) => {
        const scorePosition = d.totalScore * 0.85;
        return rScale(scorePosition) * Math.sin(angleSlice * i - Math.PI / 2);
      })
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text((d: any) => d.totalScore.toFixed(1));
  }


  prepareSpiderChartData(assessmentData) {
    // Extract relevant sections
    const userDto = assessmentData?.userDto;
    const codingResults = assessmentData?.candidateCodingResults;
    const audioScores = assessmentData?.candidateAudioScoreDto;
    // Calculate Cognitive Abilities score (average of QR, DI, VR)
    const quantitativeScore = userDto?.sectionScoreDtoList?.find(
      (s) => s.sectionName === 'Quantitative Reasoning'
    )?.totalCandidateSectionScore;
    const dataInsightScore = userDto?.sectionScoreDtoList?.find(
      (s) => s.sectionName === 'Data Insight'
    )?.totalCandidateSectionScore;
    const verbalScore = userDto?.sectionScoreDtoList?.find(
      (s) => s.sectionName === 'Verbal Reasoning'
    )?.totalCandidateSectionScore;

    // Calculate max possible scores to normalize
    const maxQuantitativeScore = 60; // 10 questions, each worth 6 points
    const maxDataInsightScore = 40; // 10 questions, each worth 4 points
    const maxVerbalScore = 50; // 10 questions, each worth 5 points

    // Normalize to percentages
    const normalizedQuantitative =
      (quantitativeScore / maxQuantitativeScore) * 100;
    const normalizedDataInsight =
      (dataInsightScore / maxDataInsightScore) * 100;
    const normalizedVerbal = (verbalScore / maxVerbalScore) * 100;

    // Calculate average cognitive score
    const cognitiveScore =
      (normalizedQuantitative + normalizedDataInsight + normalizedVerbal) / 3;

    // Convert coding final score to out of 100 (current score is out of 10)
    const codingScore = codingResults && codingResults[0]?.finalScore * 10;

    // Extract English fluency related scores
    const englishGrammarScore = audioScores?.skills.find(
      (s) => s.skillName === 'English Fluency'
    )?.totalScore;
    // const fluencyScore = audioScores?.skills.find(s => s.skillName === "Fluency")?.totalScore;
    const englishFluencyScore = englishGrammarScore;

    // Extract for FFF
    const fit_for_fractal = audioScores?.skills.find(
      (s) => s.skillName === 'Fit for Fractal'
    )?.totalScore;

    // Extract Confidence and Speech related scores
    const voiceClarityScore = audioScores?.skills.find(
      (s) => s.skillName === 'Confidence and Speech Clarity'
    )?.totalScore;
    // const confidenceScore = audioScores?.skills.find(s => s.skillName === "Confidence")?.totalScore;
    const confidenceAndSpeechScore = voiceClarityScore;

    // Technical Ability
    const technicalAbilityScore = audioScores?.skills.find(
      (s) => s.skillName === 'Technical Ability'
    )?.totalScore;

    // Extract Clarity of Thought related scores
    const clarityCoherenceScore = audioScores?.skills.find(
      (s) => s.skillName === 'Clarity of Thought'
    )?.totalScore;

    const clarityOfThoughtScore = clarityCoherenceScore;

    const radarChartData = [
      {
        skillName: 'Cognitive Abilities',
        totalScore:
          cognitiveScore != null && !isNaN(cognitiveScore)
            ? Math.round(cognitiveScore * 100) / 100
            : 0,
      },
      {
        skillName: 'Coding Skills',
        totalScore:
          codingScore != null && !isNaN(codingScore)
            ? Math.round(codingScore * 100) / 100
            : 0,
      },
      {
        skillName: 'English Fluency (Interview Bot)',
        totalScore:
          englishFluencyScore != null && !isNaN(englishFluencyScore)
            ? Math.round(englishFluencyScore * 100) / 100
            : 0,
      },
      {
        skillName: 'Confidence and Speech (Interview Bot)',
        totalScore:
          confidenceAndSpeechScore != null && !isNaN(confidenceAndSpeechScore)
            ? Math.round(confidenceAndSpeechScore * 100) / 100
            : 0,
      },
      {
        skillName: 'Clarity of Thought (Interview Bot)',
        totalScore:
          clarityOfThoughtScore != null && !isNaN(clarityOfThoughtScore)
            ? Math.round(clarityOfThoughtScore * 100) / 100
            : 0,
      },
      {
        skillName: 'Fit For Fractal (Interview Bot)',
        totalScore:
          fit_for_fractal != null && !isNaN(fit_for_fractal)
            ? Math.round(fit_for_fractal * 100) / 100
            : 0,
      },
      {
        skillName: 'Technical Abilities (Interview Bot)',
        totalScore:
          technicalAbilityScore != null && !isNaN(technicalAbilityScore)
            ? Math.round(technicalAbilityScore * 100) / 100
            : 0,
      },
    ];

    return radarChartData;
  }

  urlsResponseLength: number;
  mergeVideoStatus: any;
  isQuestionExplanationResponse: boolean;

  getAllUserScore() {
    this.loading = true;
    const payLoadObject = {
      userAssessmentDriveId: this.userAssessmentDriveId,
      sectionId: null,
    };
    this.hrService.getSectionScore(payLoadObject).subscribe({
      next: (response: any) => {
        if (response.statusCode === HttpStatusCode.Ok) {
          this.driveAssessmentData = response.data;
          this.driveReportResponseList = response.data?.reportResponseList;
          this.mergeVideoStatus = response.data?.mergedVideoStatus;
          this.urlsResponseLength = Object.keys(
            this.driveAssessmentData?.urlsForResponse ?? {}
          ).length;
          this.isQuestionExplanationResponse = (
            this.driveAssessmentData?.userDto?.sectionScoreDtoList ?? []
          ).every((video) => video?.selectedRandomQuestionExplainedUrl == null);
          // candidateAudioScoreDto is a list: one entry per scored video answer.
          this.audioScores = this.toAudioScoreList(response.data?.candidateAudioScoreDto);
          this.softSkillData =
            this.audioScores.find((entry) => entry?.skills?.length) ?? this.audioScores[0];
          this.candidateAudioData = this.softSkillData?.skills;
          if (this.candidateAudioData) {
            const spiderData = this.prepareSpiderChartData({
                ...response.data,
                candidateAudioScoreDto: this.softSkillData,
              });

            setTimeout(() => {
              this.createRadarChart(spiderData);
            });
          }
          if (this.softSkillData?.error) {
            this.showError = true;
          } else {
            this.showError = false;
          }

          this.loading = false;
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  activeTooltipIndex: string | null = null;
  tooltipPosition = { top: 0, left: 0 };

  // Updated method to capture position from the event
  showRubricTooltip(event: Event, index: string): void {
    this.activeTooltipIndex = index;

    // Position the tooltip near the cursor but with some offset
    this.tooltipPosition = {
      top: 25, // Position below the text
      left: -60, // Align with the left of the element
    };
  }

  hideRubricTooltip(): void {
    this.activeTooltipIndex = null;
  }
  detectedMonitorData: any;
  getAllDetectedMonitor() {
    this.loading = true;
    this.hrService
      .detectedMonitor(
        this.driveAssessmentData.userDto.candidateId,
        this.userAssessmentDriveId
      )
      .subscribe({
        next: (response: any) => {
          if (response.statusCode === HttpStatusCode.Ok) {
            this.detectedMonitorData = response.data;
            this.loading = false;
          } else {
            this.loading = false;
          }
        },
        error: (error) => {
          this.loading = false;
        },
      });
  }

  startMerging() {
    let data = {
      driveId: this.driveAssessmentData.driveId,
      userIds: [this.driveAssessmentData.userDto.candidateId],
    };
    this.hrService.mergeChunksForEachCandiadte(data).subscribe((response) => {
      if (response.statusCode === HttpStatusCode.Ok) {
        setTimeout(() => {
          this.getAllUserScore();
        }, 1000);
      }
    });
  }

  unMergedChunksFiles(data) {
    this.hrService
      .unMergedChunksFiles(
        this.driveAssessmentData.userDto.candidateId,
        this.driveAssessmentData.driveId,
        this.userAssessmentDriveId,
        data
      )
      .subscribe((response) => {
        if (
          (response && response.statusCode === HttpStatusCode.Ok) ||
          response.statusCode === HttpStatusCode.Created
        ) {
          if (response.data == null) {
          }
          let link = document.createElement('a');
          link.href = `data:application/json;base64,${response.data.byteArray}`;
          link.download = response.data.fileName;
          link.click();
        }
      });
  }

  isValidDate(date: any): boolean {
    return date && !isNaN(new Date(date).getTime());
  }


  currentAudioPlayer: HTMLAudioElement | null = null;

  toggleAudio(audioPlayer: HTMLAudioElement) {
    // Pause the currently playing audio if it's not the same as the clicked one
    if (this.currentAudioPlayer && this.currentAudioPlayer !== audioPlayer) {
      this.currentAudioPlayer.pause();
      this.currentAudioPlayer.currentTime = 0; // Optional: Reset to start
    }

    if (audioPlayer.paused) {
      audioPlayer.play();
      this.currentAudioPlayer = audioPlayer; // Update the current audio
    } else {
      audioPlayer.pause();
      this.currentAudioPlayer = null; // Reset if paused
    }
  }

  audioEnded(audioPlayer: HTMLAudioElement) {
    if (this.currentAudioPlayer === audioPlayer) {
      this.currentAudioPlayer = null;
    }
  }

  formatSectionStatus(status: string | null): string {
    if (!status) return '-';
    return status
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
